import React, { useState, useEffect, useMemo } from 'react';
import { ListingCard, SkeletonLoader } from '../../components';
import { FormattedMessage } from '../../util/reactIntl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import SingleLoader from './SingleLoader';
import SliderArrowControls from '../SliderArrowControls/SliderArrowControls';
import { filterOutTutulistListings } from '../../util/tutulist';

import css from './ListSlider.module.css';

const LARGE_WIDTH = 1921;
const MD_LARGE_WIDTH = 1366;
const MAX_IMAGE_LAYOUT_SCREEN_WIDTH = 1265;
const TAB_WIDTH = 767;
const MOBILE_WIDTH = 767;
const MAX_IMAGE_MOBILE_SCREEN_WIDTH = 767;
const XSMOBILE_WIDTH = 480;

const ListSlider = ({
  title = null,
  listings = [],
  queryError = null,
  isOwnListing = false,
  ...props
}) => {
  const safeListings = filterOutTutulistListings(listings);
  const { intl, viewport, isListingPageSlider, name } = props;

  const isImageLargeLayout = viewport.width > 0 && viewport.width < LARGE_WIDTH;
  const isImageMdLargeLayout =
    viewport.width > MAX_IMAGE_LAYOUT_SCREEN_WIDTH && viewport.width < MD_LARGE_WIDTH;
  const isImageTabLayout =
    viewport.width >= TAB_WIDTH && viewport.width <= MAX_IMAGE_LAYOUT_SCREEN_WIDTH;
  const isImageMMobileLayout = viewport.width > 0 && viewport.width <= MOBILE_WIDTH;
  const isImageXSMobileLayout = viewport.width > 0 && viewport.width < XSMOBILE_WIDTH;
  const isMobileLayout = viewport.width > 0 && viewport.width <= MAX_IMAGE_MOBILE_SCREEN_WIDTH;
  const isImageMobileLayout = viewport.width > 0 && viewport.width < TAB_WIDTH;

  const [activeItemIndex, setActiveItemIndex] = useState(0);
  const [slidesToShow, setSlidesToShow] = useState(4);

  useEffect(() => {
    const nextSlidesToShow = isImageXSMobileLayout
      ? 1
      : isImageMMobileLayout
      ? 2
      : isImageTabLayout
      ? 3
      : isImageMdLargeLayout
      ? 4
      : isImageLargeLayout
      ? 4
      : 4;
    setSlidesToShow(nextSlidesToShow);
  }, [
    isImageLargeLayout,
    isImageMdLargeLayout,
    isImageTabLayout,
    isImageMMobileLayout,
    isImageXSMobileLayout,
  ]);

  useEffect(() => {
    const maxStart = Math.max(safeListings.length - slidesToShow, 0);
    if (activeItemIndex > maxStart) {
      setActiveItemIndex(maxStart);
    }
  }, [safeListings.length, slidesToShow, activeItemIndex]);

  const visibleListings = useMemo(() => {
    if (isMobileLayout) {
      return safeListings;
    }
    const start = activeItemIndex;
    const end = start + slidesToShow;
    return safeListings.slice(start, end);
  }, [safeListings, isMobileLayout, activeItemIndex, slidesToShow]);

  const next = () => {
    const maxStart = Math.max(safeListings.length - slidesToShow, 0);
    setActiveItemIndex(prev => Math.min(prev + 1, maxStart));
  };

  const previous = () => {
    setActiveItemIndex(prev => Math.max(prev - 1, 0));
  };

  const maxStart = Math.max(safeListings.length - slidesToShow, 0);
  const isPrevDisabled = activeItemIndex <= 0;
  const isNextDisabled = activeItemIndex >= maxStart;

  const queryErrorMessage = queryError ? (
    <h2 className={css.error}>
      <FormattedMessage id="ListSlider.queryError" />
    </h2>
  ) : null;

  const noResults =
    safeListings.length === 0 ? (
      <div style={{ display: 'flex', width: '100%', marginBottom: '60px' }}>
        {Array(slidesToShow)
          .fill('')
          .map((item, i) => (
            <SkeletonLoader
              viewport={viewport}
              isMobile={isImageMobileLayout}
              noOfSlides={slidesToShow}
              key={i}
            />
          ))}
      </div>
    ) : null;

  const sliderButtons = (
    <SliderArrowControls
      className={classNames(css.sliderButtons, {
        [css.listingPageSliderButtons]: isListingPageSlider,
      })}
      buttonClassName={classNames(css.iconHolder, {
        [css.listingPageIconHolder]: isListingPageSlider,
      })}
      iconClassName={classNames(css.icon, {
        [css.listingPageArrowIcon]: isListingPageSlider,
      })}
      onPrev={previous}
      onNext={next}
      isPrevDisabled={isPrevDisabled}
      isNextDisabled={isNextDisabled}
    />
  );

  return (
    <div
      className={classNames(
        css.moreServices,
        props.moreServicesMobPadd,
        css.fullWidth,
        isListingPageSlider ? css.listingPageSlider : null
      )}
    >
      <div
        className={classNames(
          props.moreServicesMobPadd,
          css.moreServicesTitle,
          props.sliderHeading
        )}
      >
        {safeListings.length === 0 ? (
          <SingleLoader />
        ) : (
          <h1 className={css.sectionTitle}>
            <FormattedMessage id={title} /> <span className={css.titleHighlight}>{name}</span>
          </h1>
        )}
        {sliderButtons}
      </div>

      {queryErrorMessage}
      {noResults}
      {isMobileLayout ? (
        <div className={classNames(props.moreServicesMobPadd, css.cardSlider)}>
          {visibleListings.map((listing, i) => (
            <div className={css.homepage} key={`${listing.id?.uuid || i}`}>
              <ListingCard
                key={listing.id.uuid}
                listing={listing}
                intl={intl}
                profileSliderCardTitle={props.profileSliderCardTitle}
                profileSliderCardauthorInfo={props.profileSliderCardauthorInfo}
                profileSliderCardprice={props.profileSliderCardprice}
                homeMobileSlider={props.homeMobileSlider}
                isOwnListing={isOwnListing}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className={css.cardSlide}>
          <div
            className={css.desktopGrid}
            style={{ gridTemplateColumns: `repeat(${slidesToShow}, minmax(0, 1fr))` }}
          >
            {visibleListings.map((listing, i) => (
              <div className={css.tabletHomepage} key={`${listing.id?.uuid || i}`}>
                <ListingCard
                  key={listing.id.uuid}
                  listing={listing}
                  intl={intl}
                  profileSliderCardTitle={props.profileSliderCardTitle}
                  profileSliderCardauthorInfo={props.profileSliderCardauthorInfo}
                  profileSliderCardprice={props.profileSliderCardprice}
                  profileSliderCardWidth={props.profileSliderCardWidth}
                  isOwnListing={isOwnListing}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const { string } = PropTypes;

ListSlider.propTypes = {
  title: string.isRequired,
};
export default ListSlider;
